import React, { useState } from "react";
import { Link, Plus, X, Lightbulb, CaretDown, CaretRight, ArrowSquareOut } from "@phosphor-icons/react";
import { getCoOccurrenceSummary } from "@/lib/secondaryConditions";
import { showToast } from "@/lib/toast";
import { VIEWS } from "@/lib/navConfig";

/**
 * Aggregated co-occurrence hints across all conditions currently in the claim.
 * Surfaces top conditions the veteran has not yet added but that commonly co-occur with
 * one or more of their existing issues. Each row supports one-click ADD (as a secondary
 * to the first linked primary, by default) or DISMISS.
 *
 * Props:
 *   - issues:      Array of current claim issues
 *   - addSecondary: (suggestion, primaryIssue) => void — re-uses App.js's existing handler
 *   - setActiveView: (viewId) => void — used by the "ADD" CTA to nudge the user to the tracker
 *   - max:         number of suggestions to show by default (collapsed). Default 5.
 */
export default function CoOccurrenceHints({ issues = [], addSecondary, setActiveView, max = 5 }) {
  const [dismissed, setDismissed] = useState({});
  const [expanded, setExpanded] = useState(false);

  const summary = getCoOccurrenceSummary(issues).filter((s) => !dismissed[s.code]);
  if (issues.length === 0 || summary.length === 0) return null;

  const visible = expanded ? summary : summary.slice(0, max);
  const hiddenCount = Math.max(0, summary.length - visible.length);

  function handleAdd(suggestion) {
    // Add as secondary to the first linked primary (highest score primary in the linkedFrom list).
    const primary = suggestion.linkedFrom[0];
    // App.js's addSecondary expects (suggestion, primaryIssue). We need the actual primary issue object.
    const primaryIssue = issues.find((i) => i.id === primary.id) || { claimedAs: primary.claimedAs };
    addSecondary(
      { code: suggestion.code, name: suggestion.name, system: suggestion.system, reason: primary.reason },
      primaryIssue,
    );
    showToast({
      variant: "success",
      duration: 4500,
      message: `Added "${suggestion.name}" as secondary to ${primary.claimedAs}.`,
      action: {
        label: "OPEN",
        onClick: () => setActiveView && setActiveView(VIEWS.tracker),
      },
    });
  }

  function handleDismiss(code) {
    setDismissed((p) => ({ ...p, [code]: true }));
  }

  return (
    <div className="panel" data-testid="cooccurrence-panel">
      <div className="panel-header">
        <Lightbulb size={18} weight="bold" />
        <span>COMMONLY CO-CLAIMED — SUGGESTED NEXT CONDITIONS</span>
        <span className="header-count mono" data-testid="cooccurrence-count">{summary.length}</span>
      </div>
      <div className="panel-body">
        <p className="panel-desc">
          These conditions are commonly claimed alongside what you already have. Each suggestion is scored by how many of your current
          conditions back it — higher score = more obvious gap. <strong>Adding</strong> pre-fills the "Secondary to" theory; you can edit the theory afterward in the Claim Tracker.
        </p>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }} data-testid="cooccurrence-list">
          {visible.map((s) => (
            <li
              key={s.code}
              data-testid={`cooccurrence-row-${s.code}`}
              style={{
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                borderRadius: 4,
                padding: "12px 14px",
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                title={`Backed by ${s.score} of your conditions`}
                style={{
                  flexShrink: 0,
                  width: 36, height: 36, borderRadius: 4,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: s.score >= 3 ? "#1D4ED8" : s.score === 2 ? "#3B82F6" : "#94A3B8",
                  color: "#fff",
                  fontFamily: "JetBrains Mono, monospace",
                  fontWeight: 800,
                  fontSize: 13,
                }}
                data-testid={`cooccurrence-score-${s.code}`}
              >
                ×{s.score}
              </div>
              <div style={{ flex: "1 1 240px", minWidth: 200 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span className="mono" style={{ background: "#F1F5F9", padding: "2px 6px", borderRadius: 3, fontSize: 11, fontWeight: 700, color: "#475569" }}>DC {s.code}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{s.name}</span>
                </div>
                <div style={{ fontSize: 12, color: "#475569", marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                  <span style={{ color: "#64748B" }}>Backed by:</span>
                  {s.linkedFrom.slice(0, 3).map((p, idx) => (
                    <span key={p.id} style={{ background: "#EFF6FF", color: "#1E3A8A", borderRadius: 3, padding: "1px 6px", fontWeight: 600 }}>
                      {p.claimedAs}{idx < Math.min(s.linkedFrom.length, 3) - 1 ? "" : ""}
                    </span>
                  ))}
                  {s.linkedFrom.length > 3 && (
                    <span style={{ color: "#64748B" }}>+{s.linkedFrom.length - 3} more</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#475569", marginTop: 4, fontStyle: "italic", lineHeight: 1.4 }}>
                  {s.linkedFrom[0]?.reason}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn btn--accent btn--sm"
                  onClick={() => handleAdd(s)}
                  data-testid={`cooccurrence-add-${s.code}`}
                  title={`Add as secondary to ${s.linkedFrom[0]?.claimedAs}`}
                >
                  <Plus size={12} weight="bold" /> ADD AS SECONDARY
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => handleDismiss(s.code)}
                  data-testid={`cooccurrence-dismiss-${s.code}`}
                  aria-label={`Dismiss ${s.name}`}
                  title="Dismiss this suggestion"
                >
                  <X size={11} weight="bold" />
                </button>
              </div>
            </li>
          ))}
        </ul>
        {hiddenCount > 0 && !expanded && (
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={() => setExpanded(true)}
            data-testid="cooccurrence-show-more"
            style={{ marginTop: 12 }}
          >
            <CaretDown size={11} weight="bold" /> SHOW {hiddenCount} MORE SUGGESTIONS
          </button>
        )}
        {expanded && summary.length > max && (
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={() => setExpanded(false)}
            data-testid="cooccurrence-show-less"
            style={{ marginTop: 12 }}
          >
            <CaretRight size={11} weight="bold" /> Collapse
          </button>
        )}
        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 14, display: "flex", gap: 4, alignItems: "center" }}>
          <Link size={11} weight="bold" />
          <span>Suggestions are drawn from VA-recognized comorbidities and clinical co-occurrence patterns. Always verify with your provider before claiming.</span>
        </div>
        <a
          href="https://www.va.gov/disability/eligibility/special-claims/secondary-claims/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 11, color: "#1D4ED8", marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          VA.gov: Secondary service-connected claims <ArrowSquareOut size={10} weight="bold" />
        </a>
      </div>
    </div>
  );
}
